import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { DnsOutlined, KeyboardArrowDown, KeyboardArrowUp, RefreshOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import { useListVmsQuery, filterVmsByStatus } from '@/state/queries/vm-admin/use-list-vms';
import { VmStatus } from '@/enums/vm-status.enum';
import type { VmAdminListItem } from '@/types/dtos/vm/response/vm-admin.dto';
import VmDetailPanel from './vm-detail-panel';

const STATUS_FILTERS = [
  { i18nKey: 'admin.vmAdmin.status.all', fallback: 'All', value: '' },
  { i18nKey: 'admin.vmAdmin.status.running', fallback: 'Running', value: VmStatus.RUNNING },
  { i18nKey: 'admin.vmAdmin.status.provisioning', fallback: 'Provisioning', value: VmStatus.PROVISIONING },
  {
    i18nKey: 'admin.vmAdmin.status.containerStarted',
    fallback: 'Container Started',
    value: VmStatus.CONTAINER_STARTED,
  },
  { i18nKey: 'admin.vmAdmin.status.stopped', fallback: 'Stopped', value: VmStatus.STOPPED },
  { i18nKey: 'admin.vmAdmin.status.failed', fallback: 'Failed', value: VmStatus.FAILED },
  { i18nKey: 'admin.vmAdmin.status.terminated', fallback: 'Terminated', value: VmStatus.TERMINATED },
  { i18nKey: 'admin.vmAdmin.status.prepared', fallback: 'Prepared', value: VmStatus.PREPARED },
] as const;

const STATUS_COLOR_MAP: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  [VmStatus.RUNNING]: 'success',
  [VmStatus.PROVISIONING]: 'info',
  [VmStatus.CONTAINER_STARTED]: 'info',
  [VmStatus.PREPARED]: 'default',
  [VmStatus.STOPPED]: 'warning',
  [VmStatus.FAILED]: 'error',
  [VmStatus.TERMINATED]: 'default',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function VmRow({ vm, isExpanded, onToggle }: { vm: VmAdminListItem; isExpanded: boolean; onToggle: () => void }) {
  const statusColor = STATUS_COLOR_MAP[vm.status] ?? 'default';

  return (
    <>
      <TableRow
        hover
        onClick={onToggle}
        sx={{ 'cursor': 'pointer', '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}
      >
        <TableCell padding="checkbox" sx={{ width: 40 }}>
          <IconButton
            size="small"
            aria-label={isExpanded ? 'collapse row' : 'expand row'}
            aria-expanded={isExpanded}
            aria-controls={`vm-detail-${vm.vmId}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
            {vm.vmName}
          </Typography>
        </TableCell>
        <TableCell>{vm.profile}</TableCell>
        <TableCell>{vm.vmSize}</TableCell>
        <TableCell>
          <Chip label={vm.status} color={statusColor} size="small" variant="outlined" />
        </TableCell>
        <TableCell>{formatDate(vm.createdAt)}</TableCell>
        <TableCell>{formatDate(vm.updatedAt)}</TableCell>
        <TableCell align="right">
          {vm.costTracking ? (
            <Typography variant="body2">${vm.costTracking.estimatedTotalCostUSD.toFixed(2)}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              --
            </Typography>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={8}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit id={`vm-detail-${vm.vmId}`} role="region">
            <VmDetailPanel vm={vm} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function VmAdminPage() {
  const t = useTranslations();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [expandedVmId, setExpandedVmId] = useState<string | null>(null);

  const { data: allVms, isLoading, error, refetch, isFetching } = useListVmsQuery();

  const filtered = useMemo(() => filterVmsByStatus(allVms ?? [], statusFilter), [allVms, statusFilter]);
  const pageItems = useMemo(() => filtered.slice(page * pageSize, (page + 1) * pageSize), [filtered, page, pageSize]);

  const handleToggleExpand = useCallback((vmId: string) => {
    setExpandedVmId((prev) => (prev === vmId ? null : vmId));
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    setExpandedVmId(null);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedVmId(null);
  };

  return (
    <OrganizationContentStack
      content={
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <PageHeaderBar
            mainContent={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <DnsOutlined sx={{ fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('admin.vmAdmin.title') || 'VM Management'}
                  </Typography>
                </Box>
                <Tooltip title={t('admin.vmAdmin.refresh') || 'Refresh'}>
                  <span>
                    <IconButton onClick={() => refetch()} disabled={isFetching} size="small">
                      {isFetching ? <CircularProgress size={20} /> : <RefreshOutlined />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            }
          />

          <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            {STATUS_FILTERS.map((filter) => (
              <Chip
                key={filter.value}
                label={t(filter.i18nKey) || filter.fallback}
                size="small"
                variant={statusFilter === filter.value ? 'filled' : 'outlined'}
                color={statusFilter === filter.value ? 'primary' : 'default'}
                onClick={() => {
                  setStatusFilter(filter.value);
                  setPage(0);
                  setExpandedVmId(null);
                }}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Stack>

          <Card
            sx={{
              mt: 1.5,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <CardContent
              sx={{
                'flex': 1,
                'minHeight': 0,
                'display': 'flex',
                'flexDirection': 'column',
                'overflow': 'hidden',
                'p': 0,
                '&:last-child': { pb: 0 },
              }}
            >
              {error && !allVms ? (
                <Box sx={{ p: 4 }}>
                  <Alert severity="error">
                    {t('admin.vmAdmin.error') || 'Error loading VMs. Make sure the backend server is running.'}
                  </Alert>
                </Box>
              ) : isLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <CircularProgress size={32} />
                  <Typography sx={{ mt: 1 }}>{t('common.loading') || 'Loading...'}</Typography>
                </Box>
              ) : pageItems.length === 0 ? (
                <Box sx={{ p: 4 }}>
                  <EmptyState
                    text={
                      statusFilter
                        ? t('admin.vmAdmin.emptyFilter') || `No VMs with status "${statusFilter}"`
                        : t('admin.vmAdmin.empty') || 'No VMs found'
                    }
                    icon={<DnsOutlined sx={{ fontSize: 48, color: 'text.secondary' }} />}
                  />
                </Box>
              ) : (
                <>
                  <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ width: 40 }} />
                          <TableCell sx={{ fontWeight: 600 }}>{t('admin.vmAdmin.columns.name') || 'VM Name'}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('admin.vmAdmin.columns.profile') || 'Profile'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{t('admin.vmAdmin.columns.size') || 'Size'}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('admin.vmAdmin.columns.status') || 'Status'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('admin.vmAdmin.columns.created') || 'Created'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {t('admin.vmAdmin.columns.updated') || 'Updated'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">
                            {t('admin.vmAdmin.columns.cost') || 'Est. Cost'}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pageItems.map((vm) => (
                          <VmRow
                            key={vm.vmId}
                            vm={vm}
                            isExpanded={expandedVmId === vm.vmId}
                            onToggle={() => handleToggleExpand(vm.vmId)}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filtered.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      }
    />
  );
}
